import React, { useState } from "react";
import { ActionButton, capitalizeFirstLetter, EXCLUDED_KEYS, formatDateToReadable, StatusBadge } from "../../../utils/Helper";
import { useGetReferenceWithAnswersMutation } from "../../../store/Company/companyApiSlice";
import { useSelector } from "react-redux";
import { Modal } from "../../common";
import { useNavigate } from "react-router-dom";
import { ReactComponent as Calender } from "../../../assets/icons/calender1.svg"
import TrendIcon from "../../common/TrendIcon";

const PaymentReferenceDashboard = ({ data }) => {
    const { token } = useSelector((state) => state.auth);
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false)
    const [selectedReference, setSelectedReference] = useState([])
    const [getReferenceWithAnswers, { isLoading: isAnswerLoading }] = useGetReferenceWithAnswersMutation();

    const getQuestionAnswerData = async (row) => {
        if (row?.status === "Completed") {
            const res = await getReferenceWithAnswers({
                token,
                id: row.id
            });
            if (res?.data) {
                const questionAnswerData = res?.data?.data || {};

                const formattedQAList = Object.entries(questionAnswerData)
                    .filter(([key]) => !EXCLUDED_KEYS.includes(key))
                    .map(([question, answer]) => ({
                        question: capitalizeFirstLetter(question),
                        answer: answer && answer.trim() !== "" ? answer : "-"
                    }));
                setSelectedReference(formattedQAList ?? [])
                setIsOpen(true);
            }
        } else {
            navigate(`/dashboard/payment-ref/form`, {
                state: {
                    companyName: row?.requested_company_name,
                    id: row?.id
                }
            })
        }
    }

    const handleModalClose = () => {
        setIsOpen(false)
    }
    return (
        <>
            <div className="p-2 bg-transparent">
                <div className="bg-[#F2F4F7] p-4 rounded-[12px] border border-[#D0D5DD]">
                    {/* HEADER */}
                    <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mb-6">
                        <h1 className="text-[16px] font-[500] text-[#494E58]">
                            Review and submit payment references for companies you’ve worked with.
                        </h1>

                        {data?.stats?.lastSubmission && <div className="px-3 py-[2px] bg-[#E6EAF0] rounded-full text-[12px] text-[#3A3D42] w-fit">
                            Last Submission :{" "}
                            <span className="font-medium text-[#1D2025]">{data?.stats?.lastSubmission}</span>
                        </div>}
                    </div>

                    {/* STATS */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-2 mb-6 text-left text-[12px]">
                        {/* TOTAL REFERENCES */}
                        <div className="bg-white rounded-[12px] p-[10px] md:p-[10px] shadow-sm border">
                            <p className="text-[#494E58] mb-3">
                                Total Reference Submitted
                            </p>
                            <div className="flex gap-2 items-center text-[32px] font-semibold text-[#181D27] mb-2">
                                <Calender />{data?.stats?.totalReferences ?? 0}
                            </div>
                            <p className="flex items-center gap-1 text-[#027A48] font-semibold text-[12px]">
                                <div className={data?.stats?.percentageChange < 0 ? "scale-y-[-1]" : ""}>
                                    <TrendIcon color={data?.stats?.percentageChange < 0 ? "#DC2626" : "#027445"} />
                                </div>
                                {data?.stats?.percentageChange ?? 0}%{" "}
                                <span className="text-[#494E58]">compare to last month</span>
                            </p>
                        </div>

                        {/* MONTHLY PROGRESS */}
                        <div className="bg-white rounded-[12px] p-[10px] md:p-[10px] shadow-sm border text-[12px]">
                            <p className="text-[#494E58] mb-3">
                                This Month's Progress
                            </p>

                            <div className="text-[32px] font-semibold text-[#181D27]">
                                {data?.stats?.monthlyProgress.completed ?? 0}
                                <span className="text-[12px] font-normal  text-[#939393]">
                                    /{data?.stats?.monthlyProgress.total ?? 0} reference
                                </span>
                            </div>

                            <div className="w-full bg-[#ECEFEF] rounded-full h-2 mb-2">
                                <div
                                    className="h-2 rounded-full"
                                    style={{
                                        background: "linear-gradient(90deg, #3279A0 0%, #02263A 100%)",
                                        width: `${(data?.stats?.monthlyProgress.completed /
                                            data?.stats?.monthlyProgress.total) *
                                            100
                                            }%`
                                    }}
                                />

                            </div>

                            <p className="text-[10px] text-[#494E58]">
                                {data?.stats?.monthlyProgress.remaining} more to earn{" "}
                                <span className="text-[#027948] text-[12px] font-medium">
                                    ${data?.stats?.monthlyProgress.reward}
                                </span>{" "}
                                voucher
                            </p>
                        </div>
                    </div>

                    {/* TABLE / MOBILE CARDS */}
                    <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                        {/* DESKTOP TABLE */}
                        <div className="hidden md:block">
                            <table className="w-full text-left">
                                <thead className="bg-[#F9FBFC] text-[12px] text-[#667085]">
                                    <tr>
                                        <th className="px-6 py-4">Company Name</th>
                                        <th className="px-6 py-4">Requested On</th>
                                        <th className="px-6 py-4">Status</th>
                                        <th className="px-6 py-4">Action</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {data?.data?.map((row, index) => (
                                        <tr key={index} className="border-t text-[14px]">
                                            <td className="px-6 py-4 text-[#181D27]">
                                                {row?.requested_company_name}
                                            </td>
                                            <td className="px-6 py-4 text-[#717680]">
                                                {row?.request_date ? formatDateToReadable(row?.request_date) : "-"}
                                            </td>
                                            <td className="px-6 py-4">
                                                <StatusBadge status={row?.status} />
                                            </td>
                                            <td className="px-6 py-4 text-[#414651]">
                                                <ActionButton onClick={() => getQuestionAnswerData(row)} status={row?.status} />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* MOBILE CARDS */}
                        <div className="md:hidden divide-y">
                            {data?.data?.map((row, index) => (
                                <div key={index} className="p-4 space-y-3">
                                    <div>
                                        <p className="text-sm text-[#494E58]">Company Name</p>
                                        <p className="font-medium text-gray-900">
                                            {row?.requested_company_name}
                                        </p>
                                    </div>

                                    <div className="flex justify-between items-center">
                                        <div>
                                            <p className="text-sm text-[#494E58]">Requested On</p>
                                            <p className="text-gray-700">{row?.request_date ? formatDateToReadable(row?.request_date) : "-"}</p>
                                        </div>
                                        <StatusBadge status={row.status} />
                                    </div>

                                    <ActionButton status={row.status} fullWidth />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            {isOpen && (
                <Modal modalHandle={handleModalClose}>
                    <div className="w-full max-w-4xl mx-auto pt-4 px-2">
                        {/* HEADER */}
                        <div className="mb-4">
                            <h2 className="w-[300px] text-[18px] font-[600] text-[#181D27]">
                                Payment Reference Details
                            </h2>
                            <p className="text-[13px] text-[#667085]">
                                Submitted responses
                            </p>
                        </div>

                        {/* QUESTIONS & ANSWERS */}
                        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2 text-left">
                            {selectedReference?.map((item, index) => (
                                <div
                                    key={index}
                                    className="p-4 border border-[#E4E7EC] rounded-lg bg-[#F9FAFB]"
                                >
                                    <p className="text-[13px] text-[#667085] mb-1">
                                        {item.question}
                                    </p>
                                    <p className="text-[14px] font-[500] text-[#1D2939]">
                                        {item.answer}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </Modal>
            )}


        </>
    );
};

export default PaymentReferenceDashboard;
